/**
 * P17-T11 — `CodRefusedModal`. Confirms `out_for_delivery → cancelled`
 * for a COD order and POSTs to `/v1/admin/orders/:id/cod-refused`
 * (empty body). The 200 response (`CodRefusedResponse`) carries the
 * cancelled_at timestamp; `cancellation_reason` is server-locked to
 * `'cod_refused'`.
 *
 * UX intent (spec §8.2): operator confirms the buyer declined the
 * goods on the doorstep; driver returns with merchandise. No editable
 * fields — cancellation reason is fixed for this transition.
 *
 * Red/orange-tinted to signal a cancellation; mirrors `<RefundModal>`'s
 * danger variant on the submit button.
 */
import { useState } from "react";
import { Button, Modal } from "@wmart/ui";
import { api, ApiError } from "../lib/api-client.js";
import { useToast } from "../lib/toast.js";
import type {
  AdminOrderDetailDTO,
  CodRefusedResponse,
} from "@wmart/shared-types";

interface Props {
  order: AdminOrderDetailDTO;
  onSuccess: () => void | Promise<void>;
  onClose: () => void;
}

export function CodRefusedModal({ order, onSuccess, onClose }: Props) {
  const { notify, notifyProblem } = useToast();
  const [submitting, setSubmitting] = useState(false);

  async function onConfirm() {
    setSubmitting(true);
    try {
      await api.post<CodRefusedResponse>(
        `/v1/admin/orders/${order.id}/cod-refused`,
        {},
      );
      notify("success", "Order cancelled — COD refused");
      await onSuccess();
      onClose();
    } catch (err) {
      if (err instanceof ApiError && err.problem) notifyProblem(err.problem);
      else notify("error", "Mark refused failed");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title="Mark COD refused"
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={submitting}>
            Back
          </Button>
          <Button
            variant="danger"
            onClick={() => void onConfirm()}
            disabled={submitting}
            loading={submitting}
          >
            Mark refused
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <p className="text-body-sm text-ink-700">
          The buyer refused delivery and goods are returning with the
          driver. This will cancel order{" "}
          <span className="font-mono">{order.id.slice(0, 8)}…</span> with
          reason <strong>cod_refused</strong>.
        </p>

        <p className="text-caption text-ink-500">
          This action cannot be undone. No invoice is issued and no
          payment is recorded.
        </p>
      </div>
    </Modal>
  );
}
